/**
 * Work Orders Table Column Configuration
 *
 * Smart columns with:
 * - Status badge with color coding
 * - Urgent indicator
 * - Client/Item with search
 * - Age calculation with overdue highlighting
 * - Assigned tech lookup
 * - Estimated cost
 */

import { ReactNode } from 'react';
import type { DataTableColumn } from '../ui/DataTable/types';
import { Badge } from '../ui/Badge';
import { WORK_ORDER_STATUSES } from './StatusWorkflow';

// =============================================================================
// TYPES
// =============================================================================

export interface WorkOrderRow {
  id: number;
  status: string;
  urgent: boolean;
  clientName: string | null;
  clientId: string;
  itemName: string | null;
  qty: number;
  assignedTo: string | null;
  assignedToName: string | null;
  woRun: string | null;
  submittedBy: string | null;
  submittedByName: string | null;
  notes: string | null;
  location: string | null;
  billable: boolean;
  floral: boolean;
  deadline: string | null;
  timestamp: string;
  createdAt: string;
  // Virtual columns
  ageDays?: number;
  itemSize?: string | null;
  wholesaleCostEstimate?: number | null;
}

// =============================================================================
// STATUS HELPERS
// =============================================================================

type StatusVariant = 'warning' | 'info' | 'default' | 'success' | 'danger' | 'earth';

/**
 * Get status configuration from the centralized WORK_ORDER_STATUSES.
 * Falls back to prefix matching if exact match not found.
 */
function getStatusConfig(status: string): { variant: StatusVariant; label: string; stage: number } {
  // Try exact match first
  const found = WORK_ORDER_STATUSES.find(s => s.value === status);
  if (found) {
    return { variant: found.variant, label: found.label, stage: found.stage };
  }

  // Fallback: try prefix match (e.g., "1." matches any stage 1 status)
  const prefix = status.split('.')[0];
  const prefixMatch = WORK_ORDER_STATUSES.find(s => s.value.startsWith(prefix + '.'));
  if (prefixMatch) {
    return { variant: prefixMatch.variant, label: status, stage: prefixMatch.stage };
  }

  // Default fallback
  return { variant: 'default', label: status, stage: 0 };
}

// =============================================================================
// CELL RENDERERS
// =============================================================================

function StatusCell({ status, urgent }: { status: string; urgent: boolean }): ReactNode {
  const config = getStatusConfig(status);
  return (
    <div className="flex items-center gap-2">
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
      {urgent && (
        <Badge variant="earth" size="sm">
          Urgent
        </Badge>
      )}
    </div>
  );
}

function ItemCell({ itemName, qty, itemSize }: { itemName: string | null; qty: number; itemSize: string | null }): ReactNode {
  if (!itemName) return <span className="text-sage-400">—</span>;

  return (
    <div className="flex flex-col">
      <span className="font-medium">{itemName}</span>
      <span className="text-xs text-sage-500">
        {qty > 1 && `Qty: ${qty}`}
        {qty > 1 && itemSize && ' · '}
        {itemSize && `Size: ${itemSize}`}
      </span>
    </div>
  );
}

function AgeCell({ ageDays, deadline }: { ageDays?: number; deadline: string | null }): ReactNode {
  if (ageDays === undefined) return <span className="text-sage-400">—</span>;

  const isOverdue = deadline ? new Date(deadline) < new Date() : ageDays > 14;

  return (
    <span className={isOverdue ? 'text-red-600 font-medium' : ageDays > 7 ? 'text-amber-600' : ''}>
      {ageDays}d
    </span>
  );
}

function CostCell({ cost }: { cost?: number | null }): ReactNode {
  if (!cost) return <span className="text-sage-400">—</span>;

  return (
    <span className="font-mono">
      ${cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
    </span>
  );
}

function AssignedCell({ name, run }: { name: string | null; run: string | null }): ReactNode {
  if (!name) return <span className="text-sage-400">Unassigned</span>;

  return (
    <div className="flex flex-col">
      <span>{name}</span>
      {run && <span className="text-xs text-sage-500">{run}</span>}
    </div>
  );
}

// =============================================================================
// COLUMN CONFIGURATION
// =============================================================================

export interface WorkOrderColumnsOptions {
  /** Map of employee ID -> name for lookups */
  employeeLookup?: Map<string, string>;
  /** Whether user can see billing information */
  canSeeBilling?: boolean;
}

export function createWorkOrderColumns(
  options: WorkOrderColumnsOptions = {}
): DataTableColumn<WorkOrderRow>[] {
  const { canSeeBilling = false } = options;

  const columns: DataTableColumn<WorkOrderRow>[] = [
    // ID - usually hidden but searchable
    {
      key: 'id',
      label: 'ID',
      type: 'number',
      sortable: true,
      filterable: false,
      minWidth: 70,
      hidden: true,
      render: (value) => <span className="font-mono text-sage-500">#{value}</span>,
    },

    // Status with urgent badge
    {
      key: 'status',
      label: 'Status',
      type: 'enum',
      sortable: true,
      filterable: true,
      minWidth: 160,
      enumOptions: WORK_ORDER_STATUSES.map(s => ({
        value: s.value,
        label: s.label,
        color: s.variant === 'warning' ? 'amber' :
               s.variant === 'info' ? 'blue' :
               s.variant === 'success' ? 'green' :
               s.variant === 'danger' ? 'red' :
               s.variant === 'earth' ? 'orange' : 'gray'
      })),
      render: (_value, row) => <StatusCell status={row.status} urgent={row.urgent} />,
    },

    // Client name
    {
      key: 'clientName',
      label: 'Client',
      type: 'text',
      sortable: true,
      filterable: true,
      minWidth: 150,
      render: (value) => value || <span className="text-sage-400">—</span>,
    },

    // Item with qty and size
    {
      key: 'itemName',
      label: 'Item',
      type: 'text',
      sortable: true,
      filterable: true,
      minWidth: 180,
      render: (_value, row) => (
        <ItemCell itemName={row.itemName} qty={row.qty} itemSize={row.itemSize ?? null} />
      ),
    },

    // Assigned tech with run
    {
      key: 'assignedToName',
      label: 'Assigned',
      type: 'dynamic-enum',
      sortable: true,
      filterable: true,
      minWidth: 130,
      render: (_value, row) => (
        <AssignedCell name={row.assignedToName} run={row.woRun} />
      ),
    },

    // Age in days
    {
      key: 'ageDays' as keyof WorkOrderRow,
      label: 'Age',
      type: 'number',
      sortable: true,
      filterable: false,
      minWidth: 60,
      align: 'right',
      render: (_value, row) => <AgeCell ageDays={row.ageDays} deadline={row.deadline} />,
    },

    // Estimated cost (office only)
    ...(canSeeBilling
      ? [
          {
            key: 'wholesaleCostEstimate' as keyof WorkOrderRow,
            label: 'Est. Cost',
            type: 'currency' as const,
            sortable: true,
            filterable: false,
            minWidth: 90,
            align: 'right' as const,
            render: (value: unknown) => <CostCell cost={value as number | null} />,
          } as DataTableColumn<WorkOrderRow>,
        ]
      : []),

    // Submitted by (hidden by default)
    {
      key: 'submittedByName',
      label: 'Submitted By',
      type: 'dynamic-enum',
      sortable: true,
      filterable: true,
      minWidth: 120,
      hidden: true,
    },

    // Location (hidden by default)
    {
      key: 'location',
      label: 'Location',
      type: 'text',
      sortable: true,
      filterable: true,
      minWidth: 120,
      hidden: true,
    },

    // Notes (hidden by default)
    {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      sortable: false,
      filterable: true,
      minWidth: 200,
      hidden: true,
      render: (value) => (
        <span className="text-sm text-sage-600 line-clamp-2">{value || '—'}</span>
      ),
    },

    // Billable flag (hidden by default)
    {
      key: 'billable',
      label: 'Billable',
      type: 'boolean',
      sortable: true,
      filterable: true,
      minWidth: 80,
      hidden: true,
      render: (value) => (
        <Badge variant={value ? 'success' : 'default'} size="sm">
          {value ? 'Yes' : 'No'}
        </Badge>
      ),
    },

    // Floral flag (hidden by default)
    {
      key: 'floral',
      label: 'Floral',
      type: 'boolean',
      sortable: true,
      filterable: true,
      minWidth: 80,
      hidden: true,
      render: (value) => (
        <Badge variant={value ? 'info' : 'default'} size="sm">
          {value ? 'Yes' : 'No'}
        </Badge>
      ),
    },

    // Created date (hidden by default)
    {
      key: 'createdAt',
      label: 'Created',
      type: 'date',
      sortable: true,
      filterable: false,
      minWidth: 100,
      hidden: true,
      render: (value) => {
        if (!value) return '—';
        return new Date(value as string).toLocaleDateString('en-US', {
          timeZone: 'America/Los_Angeles',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  ];

  return columns;
}

// =============================================================================
// SEARCH CONFIGURATION
// =============================================================================

export const WORKORDER_SEARCHABLE_COLUMNS: (keyof WorkOrderRow)[] = [
  'clientName',
  'itemName',
  'assignedToName',
  'notes',
  'location',
];

// =============================================================================
// DEFAULT SORT
// =============================================================================

export const WORKORDER_DEFAULT_SORT = {
  column: 'timestamp',
  order: 'desc' as const,
};
